import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, combineLatest } from 'rxjs';
import { RideAlongService } from '../../../../services/ride-along.service';
import { TripService } from '../../../../services/trip.service';
import { ToastService } from '../../../../services/toast.service';
import { PendingInvitation } from '../../models/ride-along.model';

/**
 * Ride Along invitation surface — web equivalent of the Flutter
 * `_showRideAlongInviteDialog` glass card. Always mounted (in home-page); it
 * listens to RideAlongService.pendingInvitations$ and shows a centered glass
 * modal for the first genuine invitation, with Join / Decline / dismiss.
 *
 * Status notices (a captain removed you / you left) are surfaced as a toast and
 * marked seen rather than shown as a card.
 */
@Component({
  selector: 'app-ride-along-invite',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ride-along-invite.component.html',
  styleUrl: './ride-along-invite.component.scss',
})
export class RideAlongInviteComponent implements OnInit, OnDestroy {
  invitation: PendingInvitation | null = null;
  /** Retrieving trip details after Join is tapped. */
  joining = false;
  private acting = false;
  private handledNotices = new Set<string>();
  private subs = new Subscription();

  private static readonly NOTICE_TYPES = [
    'removed',
    'participant_removed',
    'participant_left',
    'left',
  ];

  constructor(
    private rideAlongService: RideAlongService,
    private tripService: TripService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.rideAlongService.startInvitePolling();

    this.subs.add(
      combineLatest([
        this.rideAlongService.pendingInvitations$,
        this.tripService.currentTripId$,
        this.rideAlongService.activeRideAlong$,
      ]).subscribe(([invites, currentTripId, active]) => {
        // Surface removal/left notices as toasts (once each), regardless of state.
        invites
          .filter((i) => this.isNotice(i))
          .forEach((notice) => this.surfaceNotice(notice));

        // Guardrail: never offer an invite while on your own trip or already following.
        if (currentTripId || active) {
          this.invitation = null;
          return;
        }
        if (this.joining) return;
        this.invitation = invites.find((i) => !this.isNotice(i)) ?? null;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onJoin(): void {
    const inv = this.invitation;
    if (!inv || this.acting) return;
    this.acting = true;
    this.joining = true;
    this.rideAlongService.acceptRideAlongRequest(inv.tripId, inv.requestId).subscribe({
      next: () => {
        this.acting = false;
        this.invitation = null;
        this.rideAlongService.setActiveRideAlong({
          tripId: inv.tripId,
          captainName: inv.captain.displayName,
          vesselName: inv.vesselName,
        });
        this.joining = false;
      },
      error: () => {
        // Accept can fail because we're ALREADY an active participant (common when
        // re-testing). In that case still enter follow mode rather than erroring out.
        this.acting = false;
        this.invitation = null;
        this.rideAlongService.setActiveRideAlong({
          tripId: inv.tripId,
          captainName: inv.captain.displayName,
          vesselName: inv.vesselName,
        });
        this.joining = false;
      },
    });
  }

  onDecline(): void {
    const inv = this.invitation;
    if (!inv || this.acting) return;
    this.acting = true;
    this.rideAlongService.rejectRideAlongRequest(inv.tripId, inv.requestId).subscribe({
      next: () => {
        this.acting = false;
        this.invitation = null;
      },
      error: () => {
        this.acting = false;
        // Still hide locally so the user isn't stuck with the card.
        this.rideAlongService.dismissInvitation(inv.requestId);
        this.invitation = null;
      },
    });
  }

  onDismiss(): void {
    const inv = this.invitation;
    if (!inv) return;
    this.rideAlongService.dismissInvitation(inv.requestId);
    this.invitation = null;
  }

  matesLabel(count: number | undefined): string {
    return String(count ?? 0).padStart(2, '0');
  }

  private isNotice(inv: PendingInvitation): boolean {
    const t = (inv.notificationType || '').toLowerCase();
    return RideAlongInviteComponent.NOTICE_TYPES.includes(t);
  }

  private surfaceNotice(notice: PendingInvitation): void {
    const key = notice.notificationId || notice.requestId;
    if (!key || this.handledNotices.has(key)) return;
    this.handledNotices.add(key);

    const t = (notice.notificationType || '').toLowerCase();
    const trip = notice.tripName || 'the trip';
    if (t === 'removed' || t === 'participant_removed') {
      this.toast.warning(`You were removed from ${trip}.`);
    } else {
      this.toast.info(`A mate left ${trip}.`);
    }
    if (notice.notificationId) {
      this.rideAlongService.markNotificationSeen(notice.notificationId, true).subscribe({
        error: () => {},
      });
    }
  }
}
